import { claimMethodLabel, complianceMissingLabel } from '../replies/claims.js';

/**
 * Why a piece of work is not moving, in one shape (#109). Every reason the product already stores (pauses, contact
 * status, do-not-call, reply reviews, promises, delivery and freshness reasons, withdrawals, source and mailbox health)
 * maps to one fixed code with the same copy everywhere: Home, Needs you, the customer page and Slack cards.
 *
 * Pure: the caller gathers the inputs (src/application/blockers.ts). Nothing here reads a database, a clock or a model.
 */
export const blockerCodes = [
  'no_contact', 'contact_bouncing', 'dispute_open', 'payment_claimed', 'promise_active', 'customer_replied', 'missing_po',
  'vendor_compliance', 'out_of_office', 'do_not_call', 'paused_manual', 'source_stale', 'outside_window', 'daily_cap',
  'needs_review', 'mailbox_disconnected', 'draft_withdrawn',
] as const;
export type BlockerCode = typeof blockerCodes[number];
export type BlockerOwner = 'you' | 'amy' | 'customer';
export interface BlockerSubject { kind: 'customer' | 'approval' | 'invoice' | 'workspace'; id: string }
export interface Blocker {
  code: BlockerCode;
  /** One line a finance person reads, e.g. "No working billing contact since Oct 12 (hard bounce)". */
  detail: string;
  /** ISO instant or date the condition started, when a source record says so; null when nothing stored says when. */
  since: string | null;
  owner: BlockerOwner;
  nextAction: string;
  clearsWhen: string;
  /** The page a person opens to fix it, where one exists (a hash route in the web app). */
  fix: { label: string; href: string } | null;
  subject: BlockerSubject;
}

/** Who acts and how each code clears. Copy is short and plain; the detail line carries the specifics. */
const meta: Record<BlockerCode, { owner: BlockerOwner; nextAction: string; clearsWhen: string; label: string }> = {
  no_contact: { owner: 'you', label: 'No billing contact', nextAction: 'Add a working billing contact',
    clearsWhen: 'An active billing or AP contact exists' },
  contact_bouncing: { owner: 'you', label: 'Contact bouncing', nextAction: 'Correct the contact or add another',
    clearsWhen: 'A contact that is not bouncing is on file' },
  dispute_open: { owner: 'you', label: 'Dispute open', nextAction: 'Resolve the dispute',
    clearsWhen: 'The dispute is resolved and its pause ends' },
  payment_claimed: { owner: 'amy', label: 'Payment claimed', nextAction: 'Wait for the payment to show, or ask for remittance',
    clearsWhen: 'The payment is recorded, or the hold ends' },
  promise_active: { owner: 'customer', label: 'Promise to pay', nextAction: 'None; Amy checks on the promised date',
    clearsWhen: 'The promise is kept or broken' },
  customer_replied: { owner: 'you', label: 'Customer replied', nextAction: 'Review the reply',
    clearsWhen: 'The reply is reviewed and its hold released' },
  missing_po: { owner: 'you', label: 'Missing PO', nextAction: 'Get the purchase order and resend the invoice',
    clearsWhen: 'The dispute for the missing PO is resolved' },
  vendor_compliance: { owner: 'you', label: 'Vendor compliance', nextAction: 'Complete the customer\'s vendor requirement',
    clearsWhen: 'The customer accepts the invoice' },
  out_of_office: { owner: 'customer', label: 'Out of office', nextAction: 'None; reminders resume automatically',
    clearsWhen: 'The contact is back' },
  do_not_call: { owner: 'customer', label: 'Do not call', nextAction: 'Use email only',
    clearsWhen: 'The customer agrees to calls again' },
  paused_manual: { owner: 'you', label: 'Paused', nextAction: 'Unpause when ready', clearsWhen: 'Someone ends the pause' },
  source_stale: { owner: 'you', label: 'Ledger out of date', nextAction: 'Check the integration',
    clearsWhen: 'A sync completes' },
  outside_window: { owner: 'amy', label: 'Scheduled', nextAction: 'None; it sends in the next send window',
    clearsWhen: 'The send window opens' },
  daily_cap: { owner: 'amy', label: 'Contact limit', nextAction: 'None; it sends when the limit resets',
    clearsWhen: 'The contact limit resets' },
  needs_review: { owner: 'you', label: 'Needs a person', nextAction: 'Approve, edit or skip it', clearsWhen: 'A person decides' },
  mailbox_disconnected: { owner: 'you', label: 'Mailbox disconnected', nextAction: 'Reconnect the mailbox',
    clearsWhen: 'The mailbox is connected' },
  draft_withdrawn: { owner: 'amy', label: 'Draft withdrawn', nextAction: 'None; Amy redrafts at the current balance if still owed',
    clearsWhen: 'A new draft replaces it, or nothing is owed' },
};
export const blockerLabel = (code: BlockerCode) => meta[code].label;

/** Severity for ordering: what stops all work first, then what a person must do, then what clears by itself. */
const rank: Record<BlockerCode, number> = {
  mailbox_disconnected: 0, source_stale: 1, no_contact: 2, contact_bouncing: 3, dispute_open: 4, missing_po: 5,
  vendor_compliance: 6, needs_review: 7, customer_replied: 8, paused_manual: 9, payment_claimed: 10, promise_active: 11,
  do_not_call: 12, out_of_office: 13, daily_cap: 14, outside_window: 15, draft_withdrawn: 16,
};

export function blocker(code: BlockerCode, detail: string, subject: BlockerSubject, since: string | null = null,
  fix: Blocker['fix'] = null): Blocker {
  const m = meta[code];
  return { code, detail, since, owner: m.owner, nextAction: m.nextAction, clearsWhen: m.clearsWhen, fix, subject };
}

// ---- Source mappings. Each list below is checked against its source by tests/unit/blockers-exhaustive.test.ts. ----

/** Pause reasons (pauses_reason_check, migrations/021-reply-reading.sql) and their code. Some depend on the pause id. */
export const pauseReasons = ['dispute', 'out_of_office', 'wrong_contact', 'manual', 'broken_promise', 'customer_replied'] as const;
export type PauseReason = typeof pauseReasons[number];
/** Pause id prefixes with their own meaning: the "paid upstream" source hold and the call-time payment claim. */
export const sourceHoldPrefix = 'source-hold:';
export const voicePaymentNote = 'Said on a call that payment was sent.';

export function pauseCode(pause: { id: string; reason: PauseReason; note: string }): BlockerCode {
  switch (pause.reason) {
    case 'dispute': return /^missing_po\b/.test(pause.note) ? 'missing_po' : 'dispute_open';
    case 'out_of_office': return 'out_of_office';
    case 'wrong_contact': return 'no_contact';
    case 'manual': return pause.id.startsWith(sourceHoldPrefix) ? 'payment_claimed' : 'paused_manual';
    case 'broken_promise': return 'needs_review';
    case 'customer_replied': return pause.note === voicePaymentNote ? 'payment_claimed' : 'customer_replied';
  }
}

/** Contact statuses (contacts.status CHECK, migrations/001-ledger.sql). An active contact is not a blocker. */
export const contactStatuses = ['active', 'bouncing', 'inactive'] as const;
export const contactStatusCode: Record<typeof contactStatuses[number], BlockerCode | null> = {
  active: null, bouncing: 'contact_bouncing', inactive: 'no_contact',
};

/** Withdrawal kinds (WithdrawalKind, src/application/approvals/withdraw.ts). All mean Amy withdrew the draft. */
export const withdrawalKinds = ['paid', 'partial', 'closed', 'changed'] as const;
export const withdrawalCode: Record<typeof withdrawalKinds[number], BlockerCode> = {
  paid: 'draft_withdrawn', partial: 'draft_withdrawn', closed: 'draft_withdrawn', changed: 'draft_withdrawn',
};

/** Reply reading kinds (src/domain/replies/schema.ts) waiting for review. Unreviewed injection is a person's job too. */
export const replyKindCode = (kind: string): BlockerCode => {
  if (kind === 'paid_claim' || kind === 'payment_claim') return 'payment_claimed';
  if (kind === 'vendor_compliance') return 'vendor_compliance';
  if (kind === 'dispute') return 'dispute_open';
  if (kind === 'out_of_office') return 'out_of_office';
  if (kind === 'wrong_contact') return 'no_contact';
  return 'customer_replied';
};

/**
 * Delivery, freshness and send-timing reasons are stored as sentences (deliveries.reasons, approval_withdrawals.reasons,
 * dispatch results). Each known sentence, or the fixed prefix of a templated one, maps to a code. An unknown sentence
 * returns null, and the exhaustiveness test reads the producing files so a new sentence without a mapping fails CI.
 */
export const executionReasonCodes: { match: string; prefix?: true; code: BlockerCode }[] = [
  // freshness.ts
  { match: 'Customer facts changed', code: 'draft_withdrawn' },
  { match: 'Policy changed', code: 'draft_withdrawn' },
  { match: 'Invoice ', prefix: true, code: 'draft_withdrawn' },
  { match: 'A new inbound reply arrived', code: 'customer_replied' },
  { match: 'A promise to pay became active', code: 'promise_active' },
  { match: 'A dispute or pause became active', code: 'draft_withdrawn' },
  { match: 'Reply response policy stage changed', code: 'draft_withdrawn' },
  { match: 'The policy now chooses a different action or approval', code: 'draft_withdrawn' },
  { match: 'Sender, recipient or invoice facts', prefix: true, code: 'draft_withdrawn' },
  { match: 'Draft facts or wording are no longer valid', code: 'draft_withdrawn' },
  // dispatch.ts
  { match: 'Human approval permissions changed', code: 'needs_review' },
  { match: 'Draft revision changed', code: 'draft_withdrawn' },
  { match: 'Approval no longer has a ready execution intent', code: 'draft_withdrawn' },
  { match: 'Sending is paused', code: 'paused_manual' },
  { match: 'Another delivery for this customer needs reconciliation', code: 'needs_review' },
  { match: 'Required attachment unavailable: ', prefix: true, code: 'needs_review' },
  { match: 'Delivery configuration requires attention: ', prefix: true, code: 'mailbox_disconnected' },
  { match: 'Provider rejected submission; edit and reapprove before another attempt', code: 'needs_review' },
  { match: 'Provider outcome is unknown; no automatic retry', code: 'needs_review' },
  { match: 'Delivery outcome is unknown; reconcile the saved Message-ID before further action', code: 'needs_review' },
  { match: 'Approval is ', prefix: true, code: 'draft_withdrawn' },
  // windows.ts
  { match: 'Daily contact limit reached', code: 'daily_cap' },
  { match: 'Weekly contact limit reached', code: 'daily_cap' },
  { match: 'Waiting for the customer send window', code: 'outside_window' },
];
/** The draft fact fields freshness.ts compares one by one; each produces "<field> changed". */
export const freshnessFactKeys = ['to', 'cc', 'bcc', 'from', 'contacts', 'attachments', 'subject', 'factBody', 'signature', 'footer',
  'replyTo', 'threadId', 'threadBinding', 'links'] as const;
export function executionReasonCode(reason: string): BlockerCode | null {
  if ((freshnessFactKeys as readonly string[]).some(key => reason === `${key} changed`)) return 'draft_withdrawn';
  for (const entry of executionReasonCodes) {
    if (entry.prefix ? reason.startsWith(entry.match) : reason === entry.match) return entry.code;
  }
  return null;
}

// ---- Assembly ----

const day = (iso: string) => iso.slice(0, 10);
const shortDate = (iso: string) => new Date(`${day(iso)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const since = (iso: string | null | undefined) => (iso ? ` since ${shortDate(iso)}` : '');

export interface BlockerInputs {
  customer: { id: string; status: string };
  contacts: { id: string; email: string; role: string; status: typeof contactStatuses[number]; since: string | null }[];
  pauses: { id: string; invoiceId: string | null; invoiceNumber: string | null; reason: PauseReason; note: string; until: string | null;
    active: boolean; since: string | null; by: string | null }[];
  promises: { id: string; status: string; promisedOn: string; amount: string; createdAt: string | null }[];
  doNotCall: { reason: string; since: string } | null;
  replies: { id: string; kind: string; receivedAt: string; subject: string; claimedOn?: string | null;
    claimedMethod?: string | null; complianceMissing?: string | null; invoiceId?: string | null;
    invoiceNumber?: string | null }[];
  approvals: {
    id: string; status: string; createdAt: string;
    /** True when the current policy has Amy approve this plan herself (#65); false when a person must. */
    autonomous: boolean; requiresReview: boolean;
    timing: { reason: string; until: string | null } | null;
    delivery: { status: string; reasons: string[]; at: string } | null;
    withdrawal: { note: string; at: string } | null;
  }[];
  workspace: {
    /** Set when the ledger source has not completed a sync within its threshold, or the last one failed. */
    sourceStale: { source: string; lastAt: string | null; failed: boolean } | null;
    mailboxDisconnected: boolean;
    sendingPaused: { by: string | null; since: string | null } | null;
  };
}

/**
 * The blockers for one customer and its pending work, most serious first, without duplicates of the same code on the
 * same subject. Workspace-wide blockers (mailbox, source, global pause) are included so every surface explains them.
 */
export function blockers(input: BlockerInputs): Blocker[] {
  const out: Blocker[] = [];
  const cust: BlockerSubject = { kind: 'customer', id: input.customer.id };
  const ws: BlockerSubject = { kind: 'workspace', id: 'workspace' };
  const w = input.workspace;
  if (w.mailboxDisconnected) out.push(blocker('mailbox_disconnected', 'The sending mailbox is disconnected; nothing can be sent', ws, null,
    { label: 'Reconnect', href: '#integrations' }));
  if (w.sourceStale) {
    const s = w.sourceStale;
    out.push(blocker('source_stale', s.failed ? `The last ${s.source} sync failed; sending is held to stale balances`
      : `${s.source} last synced ${s.lastAt ? shortDate(s.lastAt) : 'never'}; balances may be out of date`, ws, s.lastAt,
    { label: 'Check integration', href: '#integrations' }));
  }
  if (w.sendingPaused) out.push(blocker('paused_manual', `All sending is paused${w.sendingPaused.by ? ` by ${w.sendingPaused.by}` : ''}`, ws,
    w.sendingPaused.since));

  const reachable = input.contacts.filter(c => c.status === 'active' && ['billing', 'ap', 'primary'].includes(c.role));
  if (!reachable.length) {
    const bouncing = input.contacts.filter(c => c.status === 'bouncing');
    if (bouncing.length) {
      const latest = bouncing.map(c => c.since).filter((s): s is string => !!s).sort().at(-1) ?? null;
      out.push(blocker('contact_bouncing', `No working billing contact${since(latest)} (${bouncing.length === 1 ? 'hard bounce' : `${bouncing.length} contacts bouncing`})`,
        cust, latest, { label: 'Fix contacts', href: `#customers/${input.customer.id}` }));
    } else {
      out.push(blocker('no_contact', 'No billing contact on file', cust, null, { label: 'Add a contact', href: `#customers/${input.customer.id}` }));
    }
  }

  /** Notes are shown to people: record ids and the "Human review of <id>" wrapper are stripped, the person's words kept. */
  const readableNote = (note: string) => note.replace(/^\s*Human review of \S+/i, '')
    .replace(/\s*\(?\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b\)?/gi, '')
    .replace(/\s{2,}/g, ' ').replace(/^[\s:;,.-]+|[\s:;,-]+$/g, '').trim();
  for (const p of input.pauses.filter(x => x.active)) {
    const code = pauseCode(p);
    const subject: BlockerSubject = p.invoiceId ? { kind: 'invoice', id: p.invoiceId } : cust;
    const on = p.invoiceNumber ? ` ${p.invoiceNumber}` : '';
    const note = readableNote(p.note);
    const detail = code === 'dispute_open'
      ? `${on ? `Disputed${on}` : 'Disputes an invoice'}${since(p.since)}${note ? `: ${note.replace(/^[a-z_]+: /, '')}` : ''}`
      : code === 'missing_po' ? `Held for a missing purchase order${on}${since(p.since)}`
      : code === 'out_of_office' ? `Out of office${p.until ? ` until ${shortDate(p.until)}` : ''}`
      : code === 'no_contact' ? `Wrong contact reported${since(p.since)}`
      : code === 'paused_manual' ? `Paused${p.by ? ` by ${p.by}` : ''}${note ? `: "${note}"` : ''}`
      : code === 'needs_review' ? `Promise to pay was not kept${since(p.since)}; a person decides the next step`
      : code === 'payment_claimed' ? (p.id.startsWith(sourceHoldPrefix) ? 'The billing system shows it paid; the ledger does not yet'
        : `Customer said on a call that payment was sent${since(p.since)}`)
      : `Customer replied${since(p.since)}; reminders held`;
    out.push(blocker(code, detail, subject, p.since));
  }

  for (const r of input.replies) {
    const code = replyKindCode(r.kind);
    const refs = r.invoiceNumber ? ` for ${r.invoiceNumber}` : '';
    const detail = code === 'payment_claimed' ? (r.claimedOn
        ? `Says paid on ${shortDate(r.claimedOn)}${r.claimedMethod ? ` by ${claimMethodLabel(r.claimedMethod)}` : ''}; not in the ledger yet`
        : `Customer says they have paid (${shortDate(r.receivedAt)}); not in the ledger yet`)
      : code === 'vendor_compliance' ? `Held: missing ${complianceMissingLabel(r.complianceMissing ?? 'other')}${refs}`
      : code === 'dispute_open' ? `Customer disputes an invoice (${shortDate(r.receivedAt)})`
      : code === 'out_of_office' ? `Out-of-office reply (${shortDate(r.receivedAt)}) waiting for review`
      : code === 'no_contact' ? `Reply says this is the wrong contact (${shortDate(r.receivedAt)})`
      : `Customer replied ${shortDate(r.receivedAt)}; waiting for review`;
    const subject: BlockerSubject = (r.kind === 'payment_claim' || code === 'vendor_compliance') && r.invoiceId
      ? { kind: 'invoice', id: r.invoiceId } : { kind: 'customer', id: input.customer.id };
    out.push(blocker(code, detail, subject, r.receivedAt, { label: 'Review reply', href: '#collections' }));
  }

  for (const p of input.promises.filter(x => x.status === 'active')) {
    out.push(blocker('promise_active', `Promised ${p.amount} for ${shortDate(p.promisedOn)}`, cust, p.createdAt));
  }
  if (input.doNotCall) out.push(blocker('do_not_call', `Asked not to be called (${shortDate(input.doNotCall.since)})`, cust, input.doNotCall.since));

  for (const a of input.approvals) {
    const subject: BlockerSubject = { kind: 'approval', id: a.id };
    if (a.withdrawal) {
      out.push(blocker('draft_withdrawn', a.withdrawal.note, subject, a.withdrawal.at));
      continue;
    }
    if (a.status === 'pending' || a.status === 'snoozed') {
      // In autonomous mode Amy approves routine email herself, so a pending one is only a person's job when it is not hers.
      if (!a.autonomous || a.requiresReview) {
        out.push(blocker('needs_review', a.requiresReview ? 'Flagged for review by the policy; a person must approve'
          : 'The policy requires a person to approve this email', subject, a.createdAt, { label: 'Review', href: `#approvals/${a.id}` }));
      }
    }
    if (a.timing) {
      const code = executionReasonCode(a.timing.reason);
      if (code) out.push(blocker(code, `${a.timing.reason}${a.timing.until ? `; sends ${shortDate(a.timing.until)}` : ''}`, subject, null));
    }
    if (a.delivery && ['stale', 'failed', 'uncertain'].includes(a.delivery.status)) {
      for (const reason of a.delivery.reasons) {
        const code = executionReasonCode(reason) ?? 'needs_review';
        out.push(blocker(code, reason, subject, a.delivery.at));
      }
    }
  }

  const seen = new Set<string>();
  return out.filter(b => { const k = `${b.code}:${b.subject.kind}:${b.subject.id}`; if (seen.has(k)) return false; seen.add(k); return true; })
    .sort((x, y) => rank[x.code] - rank[y.code]);
}

/** The single blocker a row shows when space allows only one. */
export const topBlocker = (list: Blocker[]) => list[0] ?? null;
